import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  RefreshControl,
} from 'react-native';
import ReactDOM from 'react-dom';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Rect } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, KPIDeleteConfirmModal } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { goalsApi, organizationsApi, kpisApi, organizationalUnitsApi, emiratePlanApi, EmiratePlanGoal, risksApi } from '../../services/apiService';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import SingleSelectDropdown from '../../components/SingleSelectDropdown';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { KPIType, Frequency, Polarity, RiskType } from '../../types';
import { useResponsive, useRoleNavigation, useUnsavedChangesWarning } from '../../hooks';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { queryClient, queryKeys } from '../../providers/QueryProvider';
import { extractKpiErrorMessage, validateKpiRequiredFields, validateKpiMeasurementDate } from '../../utils/kpiErrors';
import { normalizeArabicNumbers } from '../../utils/rtl';

type RouteProps = RouteProp<MainStackParamList, 'AddGoal'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// KPI Form Interface
// ============================================================

interface KPIFormData {
  id: string;
  name: string;
  weight: string;
  description: string;
  kpiType: KPIType | '';
  polarity: Polarity | '';
  unit: string;
  frequency: Frequency | '';
  formula: string;
  targetValue: string;
  worstAcceptableValue: string;
  dataSource: string;
  goalWeight: string;
  startDate: string;
  isExpanded: boolean;
  hasReadings?: boolean;
  // DTS UC-02 step 4: Strategy Team picks which sections this strategic
  // KPI cascades to (multi-select). Stored as OrganizationalUnit IDs.
  assignedSections: string[];
  // DTS section 4.3: drives achievement-calculation.
  // 'independent' = manual value + mandatory evidence
  // 'aggregate'   = auto-computed weighted average from linked initiatives
  dataSourceType: 'independent' | 'aggregate' | '';
  // DTS section 4.3: for 'independent' KPIs only — the section that
  // is responsible for entering the periodic value + uploading evidence.
  // Stored as OrganizationalUnit ID (string for form state, parsed on save).
  implementingSection: string;
}

// ============================================================
// Risk Form Interface
// ============================================================
// Inline risk capture on the Add/Edit Goal page. Each risk is linked to the
// goal via related_type='strategic_goal' + related_id=<goalId> after save.
// risk_value / risk_level are computed server-side from probability * impact.

interface RiskFormData {
  id: string;            // 'new_*' for unsaved, numeric string for existing
  name: string;
  riskType: RiskType | '';
  probability: number;   // 1-5
  impact: number;        // 1-5
  expectedImpact: string;
  mitigationActions: string;
  riskOwner: string;
  isExpanded: boolean;
}

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CalendarIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const DragHandleIcon = ({ size = 20, color = '#9CA3AF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 6H8.01M8 12H8.01M8 18H8.01M16 6H16.01M16 12H16.01M16 18H16.01" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Dropdown Component
// ============================================================

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const Dropdown = ({ value, options, placeholder, onChange, disabled }: DropdownProps & { themeColors?: any }) => {
  const { colors, isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const buttonRef = useRef<View>(null);
  const selectedOption = options.find(opt => opt.value === value);

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    icon: colors.textMuted,
  };

  const handleOpen = () => {
    if (disabled) return;
    buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setButtonLayout({ x, y, width, height });
      setIsOpen(true);
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => {
      setIsOpen(false);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const portalContent = isOpen ? ReactDOM.createPortal(
    <>
      <div
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 }}
        onClick={() => setIsOpen(false)}
      />
      <div
        style={{
          position: 'fixed',
          top: buttonLayout.y + buttonLayout.height + 4,
          left: buttonLayout.x,
          width: buttonLayout.width,
          zIndex: 99999,
          backgroundColor: dropdownTheme.bg,
          border: `1px solid ${dropdownTheme.border}`,
          borderRadius: 12,
          paddingTop: 8,
          paddingBottom: 8,
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
        }}
      >
        {options.map((option) => (
          <div
            key={option.value}
            style={{ paddingTop: 10, paddingBottom: 10, paddingLeft: 16, paddingRight: 16, cursor: 'pointer', textAlign: 'right', direction: 'rtl' }}
            onClick={() => {
              onChange(option.value);
              setIsOpen(false);
            }}
          >
            <Text style={[styles.dropdownItemText, { color: dropdownTheme.text, width: '100%' }]}>{option.label}</Text>
          </div>
        ))}
      </div>
    </>,
    document.body
  ) : null;

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        ref={buttonRef as any}
        style={[styles.dropdownButton, { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border }, disabled && styles.dropdownDisabled]}
        onPress={handleOpen}
        disabled={disabled}
      >
        <ChevronDownIcon size={16} color={dropdownTheme.icon} />
        <Text style={[styles.dropdownButtonText, { color: dropdownTheme.text }, !selectedOption && { color: dropdownTheme.placeholder }]}>
          {selectedOption?.label || placeholder}
        </Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

// ============================================================
// KPI Options
// ============================================================

// KPI Options will be created inside component to use translations
// See getKpiOptions() function inside AddGoalScreen component

// ============================================================
// Main Component
// ============================================================

export default function AddGoalScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const { navigateToHome } = useRoleNavigation();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { translateOrg } = useAutoTranslate();
  const scrollViewRef = useRef<ScrollView>(null);

  // Guard on the strategic-goal capability — the same check GoalsScreen uses
  // to show the Add button — NOT the generic `isViewOnly` flag. `isViewOnly`
  // is true for anyone with hrcpms.strategic_goals.create (Strategy Team,
  // Section Heads granted the perm) because they're view-only on OPERATIONAL
  // items; but those are exactly the users allowed to create strategic goals.
  // Reusing isViewOnly here bounced them straight off this screen, so the Add
  // button appeared to do nothing.
  const canManageStrategicGoals = (
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.strategic_goals.create')
  );

  useEffect(() => {
    if (!canManageStrategicGoals) {
      if (navigation.canGoBack()) navigation.goBack();
      else navigateToHome();
    }
  }, [canManageStrategicGoals, navigation, navigateToHome]);
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding, cardPadding, sectionSpacing } = useResponsive();

  // Theme colors for form elements
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : '#F8FAFC',
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    iconColor: colors.textMuted,
    dropdown: colors.card,
    dropdownBorder: colors.border,
    dropdownText: colors.textPrimary,
    kpiCard: colors.card,
    kpiCardBorder: colors.cardBorder,
    kpiCardHeader: colors.card,
    kpiNumber: isDark ? colors.backgroundAlt : '#F1F5F9',
    cancelButton: colors.card,
    cancelButtonBorder: colors.border,
    cancelButtonText: colors.textMuted,
    checkboxBorder: isDark ? '#4B5563' : '#D1D5DB',
    checkboxBg: colors.card,
  };

  const goalId = route.params?.goalId;
  const organizationId = route.params?.organizationId;
  const unitId = route.params?.unitId;
  const scrollToKpi = route.params?.scrollToKpi;
  const isEditMode = !!goalId;
  const kpiSectionRef = useRef<View>(null);

  // KPI Options with translations
  const kpiTypeOptions: DropdownOption[] = [
    { value: 'strategic', label: t('kpis.strategic') },
  ];

  const polarityOptions: DropdownOption[] = [
    { value: 'positive', label: t('kpis.ascending') },
    { value: 'negative', label: t('kpis.descending') },
  ];

  const unitOptions: DropdownOption[] = [
    { value: '%', label: t('kpis.percentage') },
    { value: 'number', label: t('kpis.number') },
    { value: 'currency', label: t('kpis.currency') },
  ];

  const frequencyOptions: DropdownOption[] = [
    { value: 'daily', label: t('kpis.daily') },
    { value: 'weekly', label: t('kpis.weekly') },
    { value: 'monthly', label: t('kpis.monthly') },
    { value: 'quarterly', label: t('kpis.quarterly') },
    { value: 'half_yearly', label: t('kpis.semiAnnual') },
    { value: 'yearly', label: t('kpis.annual') },
  ];


  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // رمز الهدف — the "objective" segment of the operational code path.
  const [code, setCode] = useState('');

  useUnsavedChangesWarning(!!title || !!description);
  const [weight, setWeight] = useState('30');
  const [startDate, setStartDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [organization, setOrganization] = useState('');
  const [rawOrganizations, setRawOrganizations] = useState<{ id: number; name: string }[]>([]);
  const [orgUsedWeight, setOrgUsedWeight] = useState(0); // Total weight used by other goals in the org

  // Memoized translated organizations - updates when language changes
  const organizations = useMemo(() =>
    rawOrganizations.map((org) => ({
      value: org.id.toString(),
      label: translateOrg(org.name),
    })),
    [rawOrganizations, translateOrg]
  );

  // KPIs state
  const [kpis, setKpis] = useState<KPIFormData[]>([]);
  const [kpiSectionExpanded, setKpiSectionExpanded] = useState(true);
  // Sections (level-2 OrganizationalUnits) — used for the per-KPI cascade
  // multi-select. Loaded once when the screen mounts.
  const [sectionUnits, setSectionUnits] = useState<{ id: number; name: string; name_ar?: string }[]>([]);
  // DTS UC-02 step 3 — optional alignment to a Fujairah Emirate plan goal.
  const [emiratePlanGoals, setEmiratePlanGoals] = useState<EmiratePlanGoal[]>([]);
  const [selectedEmiratePlanGoal, setSelectedEmiratePlanGoal] = useState<string>('');
  const [deletedKpiIds, setDeletedKpiIds] = useState<number[]>([]); // Track KPIs to delete
  const [kpiPendingDelete, setKpiPendingDelete] = useState<{ id: string; backendId: number | null; name: string } | null>(null);

  // Risks state — inline risks captured on the goal page (open to all users).
  const [risks, setRisks] = useState<RiskFormData[]>([]);
  const [riskSectionExpanded, setRiskSectionExpanded] = useState(false);
  const [deletedRiskIds, setDeletedRiskIds] = useState<number[]>([]); // existing risks to delete

  // Loading states
  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Scroll to KPI section when navigated with scrollToKpi
  useEffect(() => {
    if (scrollToKpi && !initialLoading) {
      setKpiSectionExpanded(true);
      setTimeout(() => {
        kpiSectionRef.current?.measureLayout(
          scrollViewRef.current?.getInnerViewNode?.() || (scrollViewRef.current as any)?._innerViewRef,
          (_x: number, y: number) => {
            scrollViewRef.current?.scrollTo({ y: y - 20, animated: true });
          },
          () => {}
        );
      }, 300);
    }
  }, [scrollToKpi, initialLoading]);

  // Load all sections once — used as the option list for the per-KPI
  // cascade multi-select. We only show level-2 sections (unit_type='section')
  // because per DTS the strategy team only projects KPIs onto sections.
  useEffect(() => {
    const fetchSections = async () => {
      try {
        const resp = await organizationalUnitsApi.list({ page_size: 200 });
        const all = resp.results || [];
        const sectionsOnly = all.filter((u: any) => u.unit_type === 'section');
        setSectionUnits(sectionsOnly.map((u: any) => ({
          id: u.id,
          name: u.name,
          name_ar: u.name_ar,
        })));
      } catch (err) {
        console.error('Failed to load sections for KPI cascade:', err);
      }
    };
    fetchSections();
  }, []);

  // Load Emirate Plan goals for the optional alignment dropdown.
  useEffect(() => {
    (async () => {
      try {
        const resp = await emiratePlanApi.list({ is_active: true, page_size: 200 });
        setEmiratePlanGoals(resp.results || []);
      } catch (err) {
        console.error('Failed to load Emirate Plan goals:', err);
      }
    })();
  }, []);

  // Load organizations and goal data - use useFocusEffect to reload on every navigation
  useFocusEffect(
    useCallback(() => {
      const currentGoalId = route.params?.goalId;
      console.log('[AddGoal] Screen focused, goalId:', currentGoalId, 'scrollToKpi:', route.params?.scrollToKpi);
      loadInitialData(currentGoalId);
    }, [route.params?.goalId])
  );

  // Fetch total used weight for an organization's goals (excluding current goal in edit mode)
  const loadOrgUsedWeight = async (orgId: string, excludeGoalId?: number) => {
    try {
      const goalsResponse = await goalsApi.list({ department_id: orgId, page_size: 200 });
      const goals = goalsResponse.results || [];
      const totalUsed = goals.reduce((sum: number, g: any) => {
        if (excludeGoalId && g.id === excludeGoalId) return sum;
        return sum + (parseFloat(g.weight) || 0);
      }, 0);
      setOrgUsedWeight(Math.round(totalUsed * 100) / 100);
    } catch {
      setOrgUsedWeight(0);
    }
  };

  const loadInitialData = async (currentGoalId?: number) => {
    try {
      setInitialLoading(true);

      // الجهة picker was removed — a goal always belongs to the creator's own
      // department, for every user (no cross-department goal creation).
      let resolvedOrgId = '';
      if (user?.profile?.organization) {
        setOrganization(user.profile.organization.toString());
        resolvedOrgId = user.profile.organization.toString();
      }

      // Load goal data if editing
      const editGoalId = currentGoalId || goalId;
      if (editGoalId) {
        console.log('[AddGoal] Loading goal data for goalId:', editGoalId);
        const goalData = await goalsApi.get(editGoalId);
        console.log('[AddGoal] Goal data loaded:', goalData.title);
        setTitle(goalData.title || '');
        setDescription(goalData.description || '');
        setCode((goalData as any).code || '');
        setWeight(goalData.weight || '30');
        setStartDate(goalData.start_date || '');
        setTargetDate(goalData.target_completion_date || '');
        setOrganization(goalData.department_id?.toString() || '');
        setSelectedEmiratePlanGoal(
          (goalData as any).emirate_plan_goal != null
            ? String((goalData as any).emirate_plan_goal)
            : ''
        );
        resolvedOrgId = goalData.department_id?.toString() || '';

        // Load existing KPIs for this goal using the same endpoint as GoalDetail
        // This returns direct KPIs + KPIs from linked processes + KPIs from linked initiatives
        console.log('[AddGoal] Loading KPIs for goal:', editGoalId);
        const kpisData = await goalsApi.getKPIs(editGoalId);
        console.log('[AddGoal] KPIs loaded:', kpisData?.length, 'KPIs found');
        if (kpisData && kpisData.length > 0) {
          setKpis(kpisData.map((kpi: any) => ({
            id: kpi.id.toString(),
            name: kpi.name || '',
            weight: kpi.weight?.toString() || '30',
            description: kpi.description || '',
            kpiType: 'strategic',
            polarity: kpi.polarity || '',
            unit: kpi.unit || '',
            frequency: kpi.frequency || '',
            formula: kpi.measurement_formula || '',
            targetValue: kpi.target_value?.toString() || '0',
            worstAcceptableValue: kpi.worst_acceptable_value != null ? kpi.worst_acceptable_value.toString() : '',
            dataSource: kpi.data_source || '',
            goalWeight: '1.0',
            startDate: kpi.measurement_start_date || '',
            isExpanded: false,
            hasReadings: (kpi.readings_count || 0) > 0,
            assignedSections: Array.isArray(kpi.assigned_sections)
              ? kpi.assigned_sections.map((id: number) => id.toString())
              : [],
            dataSourceType: kpi.data_source_type || '',
            implementingSection: kpi.implementing_section
              ? String(kpi.implementing_section)
              : '',
          })));
        } else {
          setKpis([]);
        }

        // Load existing risks linked to this goal
        try {
          const risksData = await risksApi.forItem('strategic_goal', editGoalId);
          if (Array.isArray(risksData) && risksData.length > 0) {
            setRisks(risksData.map((rk: any) => ({
              id: rk.id.toString(),
              name: rk.name || '',
              riskType: rk.risk_type || '',
              probability: rk.probability || 3,
              impact: rk.impact || 3,
              expectedImpact: rk.expected_impact || '',
              mitigationActions: rk.mitigation_actions || '',
              riskOwner: rk.risk_owner || '',
              isExpanded: false,
            })));
            setRiskSectionExpanded(true);
          } else {
            setRisks([]);
          }
        } catch (riskErr) {
          console.warn('[AddGoal] Failed to load risks:', riskErr);
        }
      }

      // Load org weight usage
      if (resolvedOrgId) {
        await loadOrgUsedWeight(resolvedOrgId, editGoalId);
      }
    } catch (error: any) {
      showError(t('common.loadingError'));
      console.error('[AddGoal] Error loading data:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  // Generate unique ID for new KPIs
  const generateId = () => `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Add new KPI
  const addKpi = () => {
    const newKpi: KPIFormData = {
      id: generateId(),
      name: '',
      weight: '30',
      description: '',
      kpiType: 'strategic',
      polarity: '',
      unit: '',
      frequency: '',
      formula: '',
      targetValue: '0.0',
      worstAcceptableValue: '',
      dataSource: '',
      goalWeight: '1.0',
      startDate: '',
      isExpanded: true,
      assignedSections: [],
      dataSourceType: '',
      implementingSection: '',
    };
    setKpis([...kpis, newKpi]);
  };

  // Update KPI
  const updateKpi = (id: string, field: keyof KPIFormData, value: any) => {
    setKpis(kpis.map(kpi => (kpi.id === id ? { ...kpi, [field]: value } : kpi)));
  };

  // Remove KPI — open confirmation first
  const removeKpi = (id: string) => {
    const target = kpis.find(k => k.id === id);
    const backendId = !id.startsWith('new_') ? parseInt(id, 10) : null;
    setKpiPendingDelete({
      id,
      backendId: backendId !== null && !isNaN(backendId) ? backendId : null,
      name: (target as any)?.name || '',
    });
  };

  const confirmRemoveKpi = () => {
    if (!kpiPendingDelete) return;
    const { id, backendId } = kpiPendingDelete;
    if (backendId !== null) {
      setDeletedKpiIds(prev => [...prev, backendId]);
    }
    setKpis(prevKpis => prevKpis.filter(kpi => kpi.id !== id));
    setKpiPendingDelete(null);
  };

  // Toggle KPI expansion
  const toggleKpiExpansion = (id: string) => {
    setKpis(kpis.map(kpi => (kpi.id === id ? { ...kpi, isExpanded: !kpi.isExpanded } : kpi)));
  };

  // ---- Risk handlers (inline risk capture, open to all users) ----
  const addRisk = () => {
    const newRisk: RiskFormData = {
      id: generateId(),
      name: '',
      riskType: '',
      probability: 3,
      impact: 3,
      expectedImpact: '',
      mitigationActions: '',
      riskOwner: '',
      isExpanded: true,
    };
    setRisks(prev => [...prev, newRisk]);
    setRiskSectionExpanded(true);
  };

  const updateRisk = (id: string, field: keyof RiskFormData, value: any) => {
    setRisks(prev => prev.map(r => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const removeRisk = (id: string) => {
    if (!id.startsWith('new_')) {
      const backendId = parseInt(id, 10);
      if (!isNaN(backendId)) setDeletedRiskIds(prev => [...prev, backendId]);
    }
    setRisks(prev => prev.filter(r => r.id !== id));
  };

  const toggleRiskExpansion = (id: string) => {
    setRisks(prev => prev.map(r => (r.id === id ? { ...r, isExpanded: !r.isExpanded } : r)));
  };

  // Validate form
  const validateForm = () => {
    if (!title.trim()) {
      showError(t('validation.goalNameRequired'));
      return false;
    }
    if (!description.trim()) {
      showError(t('validation.goalDescriptionRequired'));
      return false;
    }
    if (!startDate) {
      showError(t('validation.startDateRequired'));
      return false;
    }
    if (!targetDate) {
      showError(t('validation.targetDateRequired'));
      return false;
    }
    if (startDate && targetDate && startDate > targetDate) {
      showError(t('validation.startDateBeforeTarget'));
      return false;
    }
    // Validate KPI required fields
    const kpiError = validateKpiRequiredFields(kpis, t);
    if (kpiError) {
      showError(kpiError);
      return false;
    }
    // Validate KPI measurement start date does not exceed goal's target completion date
    const dateError = validateKpiMeasurementDate(kpis, targetDate);
    if (dateError) {
      showError(dateError);
      return false;
    }
    return true;
  };

  // Save goal
  const handleSave = async () => {
    if (!validateForm()) return;

    // DTS UC-02 step 4 — sum of strategic KPI weights must NOT EXCEED 100%.
    // Drafts under 100% are allowed; only block when total > 100.
    const strategicKpis = kpis.filter(k => k.kpiType === 'strategic' && k.name.trim());
    if (strategicKpis.length > 0) {
      const total = strategicKpis.reduce(
        (sum, k) => sum + (parseFloat(k.weight) || 0),
        0,
      );
      if (total > 100.01) {
        showError(
          `مجموع أوزان المؤشرات الاستراتيجية لا يمكن أن يتجاوز 100%. الحالي: ${total.toFixed(2)}%`,
        );
        return;
      }

      // DTS 4.3 level 3: every strategic KPI must declare its data-source
      // type (independent / aggregate) so the engine knows how to compute
      // its achievement.
      const missing = strategicKpis.find(k => !k.dataSourceType);
      if (missing) {
        showError(
          `يجب اختيار نوع مصدر البيانات (مستقل أو تجميعي) للمؤشر الاستراتيجي: "${missing.name}".`,
        );
        return;
      }
    }

    try {
      setSaving(true);

      const goalData: any = {
        title: title.trim(),
        description: description.trim(),
        code: code.trim(),
        weight: parseFloat(weight) || 30,
        start_date: startDate,
        target_completion_date: targetDate,
        // DTS UC-02 step 3 — optional alignment to a Fujairah Emirate plan goal.
        emirate_plan_goal: selectedEmiratePlanGoal
          ? parseInt(selectedEmiratePlanGoal, 10)
          : null,
      };

      // Set department_id (backend model field). `organization` holds the
      // creator's own department in create mode and the goal's existing
      // department in edit mode — the الجهة picker was removed.
      if (organization) {
        goalData.department_id = parseInt(organization);
      } else if (user?.profile?.organization) {
        goalData.department_id = user.profile.organization;
      }

      let savedGoalId: number;

      // Check if user is org_employee (needs to submit for approval)
      const isOrgEmployee = user?.position_type === 'EMPLOYEE';

      if (isEditMode && goalId) {
        await goalsApi.update(goalId, goalData);
        savedGoalId = goalId;
        showSuccess(t('goals.updateSuccess'));
      } else {
        const response = await goalsApi.create(goalData);
        savedGoalId = response.id;

        // Backend perform_create auto-submits as pending for org_employee
        if (isOrgEmployee) {
          showSuccess(t('goals.createAndSubmitSuccess'));
        } else {
          showSuccess(t('goals.createSuccess'));
        }
      }

      // Link goal to organizational unit if created from unit detail
      if (unitId && !isEditMode) {
        try {
          const unitData = await organizationalUnitsApi.get(unitId);
          const existingGoalIds = unitData.strategic_goal_ids || [];
          await organizationalUnitsApi.update(unitId, {
            strategic_goal_ids: [...existingGoalIds, savedGoalId],
          } as any);
        } catch (linkError) {
          console.error('Failed to link goal to unit:', linkError);
        }
      }

      // Delete removed KPIs
      let deletedCount = 0;
      let deleteFailed = 0;

      if (deletedKpiIds.length > 0) {
        for (const kpiId of deletedKpiIds) {
          try {
            await kpisApi.delete(kpiId);
            deletedCount++;
          } catch (deleteError: any) {
            deleteFailed++;
          }
        }
      }

      // Show message about deletions
      if (deletedCount > 0) {
        showSuccess(t('kpis.deletedCount', { count: deletedCount }));
      }
      if (deleteFailed > 0) {
        showError(t('kpis.deleteFailed'));
      }

      // Save KPIs - only create new ones and update modified existing ones
      const kpiErrors: string[] = [];
      for (const kpi of kpis) {
        if (kpi.name.trim()) {
          const kpiData: any = {
            strategic_goal: savedGoalId,
            name: kpi.name.trim(),
            description: kpi.description.trim(),
            kpi_type: kpi.kpiType || 'strategic',
            polarity: kpi.polarity || 'positive',
            frequency: kpi.frequency || 'monthly',
            unit: kpi.unit || '%',
            target_value: (parseFloat(kpi.targetValue) || 0).toFixed(2),
            worst_acceptable_value:
              kpi.polarity === 'negative' && kpi.unit !== '%' && kpi.worstAcceptableValue !== ''
                ? (parseFloat(kpi.worstAcceptableValue) || 0).toFixed(2)
                : null,
            weight: parseFloat(kpi.weight) || 30,
            measurement_formula: kpi.formula.trim(),
            data_source: kpi.dataSource.trim(),
            measurement_start_date: kpi.startDate || null,
            source_type: 'strategic_goal',
            // DTS UC-02 step 4 — sections this strategic KPI is cascaded onto.
            assigned_sections: kpi.assignedSections.map((id: string) => parseInt(id, 10)),
            // DTS section 4.3 — independent vs aggregate.
            data_source_type: kpi.dataSourceType || '',
            // Implementing section only meaningful when source is independent.
            implementing_section: kpi.dataSourceType === 'independent' && kpi.implementingSection
              ? parseInt(kpi.implementingSection, 10)
              : null,
          };

          // Check if it's an existing KPI (has numeric id) or new
          if (!kpi.id.startsWith('new_')) {
            try {
              await kpisApi.update(parseInt(kpi.id), kpiData);
            } catch (updateError: any) {
              const errMsg = extractKpiErrorMessage(updateError?.response?.data);
              kpiErrors.push(t('kpis.kpiUpdateError', { name: kpi.name, error: errMsg }));
              console.warn(`KPI ${kpi.id} update failed:`, updateError?.response?.data);
            }
          } else {
            try {
              await kpisApi.create(kpiData);
            } catch (createError: any) {
              const errMsg = extractKpiErrorMessage(createError?.response?.data);
              kpiErrors.push(t('kpis.kpiSaveError', { name: kpi.name, error: errMsg }));
              console.warn(`KPI create failed:`, createError?.response?.data);
            }
          }
        }
      }

      if (kpiErrors.length > 0) {
        showError(kpiErrors.join('\n'));
      }

      // Delete removed risks (edit mode)
      for (const riskId of deletedRiskIds) {
        try {
          await risksApi.delete(riskId);
        } catch (delErr) {
          console.warn('Risk delete failed:', riskId, delErr);
        }
      }

      // Save risks — create new ones, update existing. Only rows with a name
      // and a selected type are persisted (partial rows are silently skipped).
      const riskErrors: string[] = [];
      for (const risk of risks) {
        if (!risk.name.trim() || !risk.riskType) continue;
        const riskData: any = {
          name: risk.name.trim(),
          related_type: 'strategic_goal',
          related_id: savedGoalId,
          risk_type: risk.riskType,
          probability: risk.probability,
          impact: risk.impact,
          expected_impact: risk.expectedImpact.trim(),
          mitigation_actions: risk.mitigationActions.trim(),
          risk_owner: risk.riskOwner.trim(),
        };
        try {
          if (risk.id.startsWith('new_')) {
            await risksApi.create(riskData);
          } else {
            await risksApi.update(parseInt(risk.id, 10), riskData);
          }
        } catch (riskErr: any) {
          console.warn('Risk save failed:', risk.name, riskErr?.response?.data);
          riskErrors.push(risk.name.trim());
        }
      }
      if (riskErrors.length > 0) {
        showError(`${t('risks.saveFailed')}: ${riskErrors.join('، ')}`);
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.goals.all });
      if (isEditMode) {
        navigation.goBack();
      } else {
        navigation.navigate('GoalDetail', { id: savedGoalId });
      }
    } catch (error: any) {
      showError(error.response?.data?.detail || t('goals.saveFailed'));
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (initialLoading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: containerPadding }
        ]}
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('goals.strategicGoals'), route: 'Goals' },
            { label: isEditMode ? t('goals.editGoal') : t('goals.addNewGoal') },
          ]}
        />

        {/* Main Card */}
        <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder }]}>
          {/* Page Title */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>
            {isEditMode ? t('goals.editGoal') : t('goals.addNewGoal')}
          </Text>

          {/* Basic Information Section */}
          <View style={[styles.section, { backgroundColor: themeColors.section }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('goals.basicInfo')}</Text>

            {/* الجهة is no longer user-selectable — every strategic goal belongs
                to the creator's own department (auto-assigned on save). */}

            {/* Name & Weight Row */}
            <View style={[styles.row, isMobile && styles.rowMobile]}>
              {/* Weight Field */}
              <View style={styles.fieldSmall}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('common.weight')}</Text>
                </View>
                <View style={styles.weightInputRow}>
                  <TouchableOpacity style={[styles.setWeightButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    <Text style={[styles.setWeightButtonText, { color: themeColors.inputText }]}>{t('goals.setWeight')}</Text>
                  </TouchableOpacity>
                  <Text style={[styles.percentSymbol, { color: themeColors.iconColor }]}>%</Text>
                  <TextInput
                    style={[styles.weightInput, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                    value={weight}
                    onChangeText={(v) => setWeight(normalizeArabicNumbers(v))}
                    keyboardType="numeric"
                    textAlign="right"
                  />
                </View>
                {/* Weight Usage Indicator */}
                {(() => {
                  const currentWeight = parseFloat(weight) || 0;
                  const totalWithCurrent = orgUsedWeight + currentWeight;
                  const isOver = totalWithCurrent > 100;
                  const barPercent = Math.min(totalWithCurrent, 100);
                  const barColor = isOver ? '#EF4444' : totalWithCurrent > 80 ? '#EAB308' : '#22C55E';
                  return (
                    <View style={styles.weightCapContainer}>
                      <View style={styles.weightCapBar}>
                        <View style={[styles.weightCapFill, { width: `${barPercent}%`, backgroundColor: barColor }]} />
                      </View>
                      <Text style={[styles.weightCapText, { color: isOver ? '#EF4444' : themeColors.textMuted }]}>
                        {t('goals.weightUsed')}: {Math.round(totalWithCurrent)}% / 100%
                        {isOver ? ` (${t('goals.weightExceeded')})` : ` (${t('goals.weightRemaining')}: ${Math.round(100 - totalWithCurrent)}%)`}
                      </Text>
                    </View>
                  );
                })()}
              </View>

              {/* Name Field */}
              <View style={styles.fieldLarge}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('common.name')}</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={title}
                  onChangeText={setTitle}
                  placeholder={t('goals.goalNamePlaceholder')}
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
              </View>
            </View>

            {/* Description Field */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('common.description')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={description}
                onChangeText={setDescription}
                placeholder=""
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Goal Code (رمز الهدف) — "objective" segment of the code path */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('operations.operationCode')}</Text>
              </View>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={code}
                onChangeText={setCode}
                placeholder={t('operations.operationCodeHint')}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
              />
            </View>

            {/* Date Fields Row - RTL: target date on left, start date on right */}
            <View style={{ flexDirection: 'row', gap: 12 }}>
              {/* Target Date Field */}
              <View style={[styles.field, { flex: 1 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('goals.targetDate')}</Text>
                </View>
                <View style={[styles.dateInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                  {Platform.OS === 'web' ? (
                    <input
                      type="date"
                      value={targetDate}
                      onChange={(e) => setTargetDate(e.target.value)}
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        backgroundColor: 'transparent',
                        fontSize: 14,
                        fontFamily: 'inherit',
                        color: themeColors.inputText,
                        textAlign: 'right',
                        direction: 'rtl',
                        padding: '10px 0',
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <>
                      <CalendarIcon size={16} color={themeColors.iconColor} />
                      <TextInput
                        style={[styles.dateInput, { color: themeColors.inputText }]}
                        value={targetDate}
                        onChangeText={setTargetDate}
                        placeholder="YYYY-MM-DD"
                        placeholderTextColor={themeColors.placeholder}
                        textAlign="right"
                      />
                    </>
                  )}
                </View>
              </View>

              {/* Start Date Field */}
              <View style={[styles.field, { flex: 1 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('goals.startDate')}</Text>
                </View>
                <View style={[styles.dateInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                  {Platform.OS === 'web' ? (
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        backgroundColor: 'transparent',
                        fontSize: 14,
                        fontFamily: 'inherit',
                        color: themeColors.inputText,
                        textAlign: 'right',
                        direction: 'rtl',
                        padding: '10px 0',
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <>
                      <CalendarIcon size={16} color={themeColors.iconColor} />
                      <TextInput
                        style={[styles.dateInput, { color: themeColors.inputText }]}
                        value={startDate}
                        onChangeText={setStartDate}
                        placeholder="YYYY-MM-DD"
                        placeholderTextColor={themeColors.placeholder}
                        textAlign="right"
                      />
                    </>
                  )}
                </View>
              </View>
            </View>

            {/* DTS UC-02 step 3 — optional alignment to a Fujairah Emirate
                plan goal. Native <select> for web; rendered as a simple
                fallback elsewhere. */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  ربط بهدف من خطة إمارة الفجيرة (اختياري)
                </Text>
              </View>
              {Platform.OS === 'web' ? (
                <select
                  value={selectedEmiratePlanGoal}
                  onChange={(e) => setSelectedEmiratePlanGoal(e.target.value)}
                  dir="rtl"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${themeColors.inputBorder}`,
                    borderRadius: 8,
                    backgroundColor: themeColors.input,
                    color: themeColors.inputText,
                    fontSize: 14,
                    fontFamily: 'inherit',
                    textAlign: 'right',
                    minHeight: 44,
                    cursor: 'pointer',
                    outline: 'none',
                  }}
                >
                  <option value="">— لا ربط —</option>
                  {emiratePlanGoals.map((g) => (
                    <option key={g.id} value={String(g.id)}>
                      {g.code ? `[${g.code}] ` : ''}{g.title}{g.year ? ` (${g.year})` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <Text style={{ padding: 10, color: themeColors.inputText }}>
                  {selectedEmiratePlanGoal
                    ? emiratePlanGoals.find(g => String(g.id) === selectedEmiratePlanGoal)?.title || ''
                    : '— لا ربط —'}
                </Text>
              )}
            </View>
          </View>

          {/* KPIs Section */}
          <View ref={kpiSectionRef} style={[styles.kpiSection, { backgroundColor: themeColors.section }]}>
            {/* Section Header */}
            <TouchableOpacity
              style={styles.kpiSectionHeader}
              onPress={() => setKpiSectionExpanded(!kpiSectionExpanded)}
            >
              {kpiSectionExpanded ? (
                <ChevronUpIcon size={20} color={themeColors.iconColor} />
              ) : (
                <ChevronDownIcon size={20} color={themeColors.iconColor} />
              )}
              <Text style={[styles.kpiSectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.strategicKPIs')}</Text>
            </TouchableOpacity>

            {kpiSectionExpanded && (
              <View style={styles.kpiSectionContent}>
                {/* KPI Cards */}
                {kpis.map((kpi, index) => (
                  <View key={kpi.id} style={[styles.kpiCard, { backgroundColor: themeColors.kpiCard, borderColor: themeColors.kpiCardBorder }]}>
                    {/* KPI Header */}
                    <View style={[styles.kpiCardHeader, { backgroundColor: themeColors.kpiCardHeader, borderBottomColor: themeColors.kpiCardBorder }]}>
                      <View style={styles.kpiCardHeaderLeft}>
                        <TouchableOpacity
                          style={styles.kpiHeaderButton}
                          onPress={() => toggleKpiExpansion(kpi.id)}
                        >
                          {kpi.isExpanded ? (
                            <ChevronUpIcon size={20} color={themeColors.iconColor} />
                          ) : (
                            <ChevronDownIcon size={20} color={themeColors.iconColor} />
                          )}
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={styles.kpiHeaderButton}
                          onPress={() => removeKpi(kpi.id)}
                        >
                          <TrashIcon size={18} color={themeColors.iconColor} />
                        </TouchableOpacity>
                      </View>
                      <View style={styles.kpiCardHeaderRight}>
                        <Text style={[styles.kpiCardName, { color: themeColors.inputText }]}>
                          {kpi.name || `${t('kpis.kpiName')} ${index + 1}`}
                        </Text>
                        <View style={[styles.kpiCardNumber, { backgroundColor: themeColors.kpiNumber }]}>
                          <Text style={[styles.kpiCardNumberText, { color: themeColors.inputText }]}>{index + 1}</Text>
                        </View>
                        <DragHandleIcon size={20} color={themeColors.iconColor} />
                      </View>
                    </View>

                    {/* KPI Content - Expanded */}
                    {kpi.isExpanded && (
                      <View style={styles.kpiCardContent}>
                        {/* Basic Information Subsection */}
                        <View style={[styles.subsection, { backgroundColor: themeColors.section }]}>
                          <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.basicInformation')}</Text>

                          {/* Name & Weight Row */}
                          <View style={[styles.row, isMobile && styles.rowMobile]}>
                            {/* Weight */}
                            <View style={styles.fieldSmall}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('common.weight')}</Text>
                              </View>
                              <View style={styles.weightInputRow}>
                                <TouchableOpacity style={[styles.setWeightButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                                  <Text style={[styles.setWeightButtonText, { color: themeColors.inputText }]}>{t('kpis.setWeight')}</Text>
                                </TouchableOpacity>
                                <Text style={[styles.percentSymbol, { color: themeColors.iconColor }]}>%</Text>
                                <TextInput
                                  style={[styles.weightInput, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                                  value={kpi.weight}
                                  onChangeText={(v) => updateKpi(kpi.id, 'weight', normalizeArabicNumbers(v))}
                                  keyboardType="numeric"
                                  textAlign="right"
                                />
                              </View>
                            </View>

                            {/* Name */}
                            <View style={styles.fieldLarge}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiName')}</Text>
                              </View>
                              <TextInput
                                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                                value={kpi.name}
                                onChangeText={(v) => updateKpi(kpi.id, 'name', v)}
                                placeholder=""
                                textAlign="right"
                              />
                            </View>
                          </View>

                          {/* Description */}
                          <View style={styles.field}>
                            <View style={styles.labelRow}>
                              <Text style={styles.requiredStar}>*</Text>
                              <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.description')}</Text>
                            </View>
                            <TextInput
                              style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                              value={kpi.description}
                              onChangeText={(v) => updateKpi(kpi.id, 'description', v)}
                              placeholder=""
                              textAlign="right"
                              multiline
                              numberOfLines={3}
                            />
                          </View>
                        </View>

                        {/* Classification Subsection */}
                        <View style={[styles.subsection, { zIndex: 30, backgroundColor: themeColors.section }]}>
                          <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.classification')}</Text>

                          <View style={[styles.row, isMobile && styles.rowMobile, { zIndex: 10 }]}>
                            {/* Polarity */}
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.polarity')}</Text>
                              </View>
                              <Dropdown
                                value={kpi.polarity}
                                options={polarityOptions}
                                placeholder={t('kpis.selectPolarityHint')}
                                onChange={(v) => updateKpi(kpi.id, 'polarity', v)}
                              />
                            </View>

                            {/* KPI Type */}
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiClassification')}</Text>
                              </View>
                              <Dropdown
                                value={'strategic'}
                                options={kpiTypeOptions}
                                placeholder={t('kpis.selectClassification')}
                                onChange={(v) => updateKpi(kpi.id, 'kpiType', v)}
                                disabled={true}
                              />
                            </View>
                          </View>

                        </View>

                        {/* Measurement Subsection */}
                        <View style={[styles.subsection, { zIndex: 20, backgroundColor: themeColors.section }]}>
                          <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.measurement')}</Text>

                          {/* Start Date */}
                          <View style={styles.field}>
                            <View style={styles.labelRow}>
                              <Text style={styles.requiredStar}>*</Text>
                              <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.measurementStartDate')}</Text>
                            </View>
                            <View style={[styles.dateInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                              {Platform.OS === 'web' ? (
                                <input
                                  type="date"
                                  value={kpi.startDate}
                                  onChange={(e) => updateKpi(kpi.id, 'startDate', e.target.value)}
                                  style={{
                                    flex: 1,
                                    border: 'none',
                                    outline: 'none',
                                    backgroundColor: 'transparent',
                                    fontSize: 14,
                                    fontFamily: 'inherit',
                                    color: themeColors.inputText,
                                    textAlign: 'right',
                                    direction: 'rtl',
                                    padding: '10px 0',
                                    cursor: 'pointer',
                                  }}
                                />
                              ) : (
                                <>
                                  <CalendarIcon size={16} color={themeColors.iconColor} />
                                  <TextInput
                                    style={[styles.dateInput, { color: themeColors.inputText }]}
                                    value={kpi.startDate}
                                    onChangeText={(v) => updateKpi(kpi.id, 'startDate', v)}
                                    placeholder="DD-MM-YYYY"
                                    placeholderTextColor={themeColors.placeholder}
                                    textAlign="right"
                                  />
                                </>
                              )}
                            </View>
                          </View>

                          {/* Row 2: Unit & Frequency */}
                          <View style={[styles.row, isMobile && styles.rowMobile, { zIndex: 10 }]}>
                            {/* Frequency */}
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.measurementFrequency')}</Text>
                              </View>
                              <Dropdown
                                value={kpi.frequency}
                                options={frequencyOptions}
                                placeholder={t('kpis.selectFrequency')}
                                onChange={(v) => {
                                  if (kpi.hasReadings) {
                                    showError(t('kpis.cannotChangeFrequency'));
                                    return;
                                  }
                                  updateKpi(kpi.id, 'frequency', v);
                                }}
                                disabled={kpi.hasReadings}
                              />
                            </View>

                            {/* Unit */}
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.measurementUnit')}</Text>
                              </View>
                              <Dropdown
                                value={kpi.unit}
                                options={unitOptions}
                                placeholder={t('kpis.selectUnitHint')}
                                onChange={(v) => updateKpi(kpi.id, 'unit', v)}
                              />
                            </View>
                          </View>

                          {/* Formula */}
                          <View style={styles.field}>
                            <View style={styles.labelRow}>
                              <Text style={styles.requiredStar}>*</Text>
                              <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.measurementFormula')}</Text>
                            </View>
                            <TextInput
                              style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                              value={kpi.formula}
                              onChangeText={(v) => updateKpi(kpi.id, 'formula', v)}
                              placeholder={t('kpis.formulaPlaceholder')}
                              placeholderTextColor={themeColors.placeholder}
                              textAlign="right"
                            />
                          </View>

                          {/* Row 3: Target Value & Data Source */}
                          <View style={[styles.row, isMobile && styles.rowMobile]}>
                            {/* Data Source */}
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.dataSource')}</Text>
                              </View>
                              <TextInput
                                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                                value={kpi.dataSource}
                                onChangeText={(v) => updateKpi(kpi.id, 'dataSource', v)}
                                placeholder={t('kpis.dataSourcePlaceholder')}
                                placeholderTextColor={themeColors.placeholder}
                                textAlign="right"
                              />
                            </View>

                            {/* Target Value */}
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.targetValue')}</Text>
                              </View>
                              <TextInput
                                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                                value={kpi.targetValue}
                                onChangeText={(v) => updateKpi(kpi.id, 'targetValue', normalizeArabicNumbers(v))}
                                placeholder="0.0"
                                placeholderTextColor={themeColors.placeholder}
                                keyboardType="numeric"
                                textAlign="right"
                              />
                            </View>
                          </View>

                          {/* Worst Acceptable Value — only for negative polarity + non-% unit */}
                          {kpi.polarity === 'negative' && kpi.unit && kpi.unit !== '%' && (
                            <View style={styles.formRow}>
                              <View style={styles.fieldHalf}>
                                <View style={styles.labelRow}>
                                  {parseFloat(kpi.targetValue) === 0 && (
                                    <Text style={styles.requiredStar}>*</Text>
                                  )}
                                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.worstAcceptableValue')}</Text>
                                </View>
                                <TextInput
                                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                                  value={kpi.worstAcceptableValue}
                                  onChangeText={(v) => updateKpi(kpi.id, 'worstAcceptableValue', normalizeArabicNumbers(v))}
                                  placeholder={t('kpis.worstAcceptableValueHint')}
                                  placeholderTextColor={themeColors.placeholder}
                                  keyboardType="numeric"
                                  textAlign="right"
                                />
                              </View>
                            </View>
                          )}

                          {/* Data source type — DTS section 4.3 (single-select dropdown) */}
                          <View style={{ marginTop: 12 }}>
                            <View style={styles.labelRow}>
                              <Text style={styles.requiredStar}>*</Text>
                              <Text style={[styles.label, { color: themeColors.label }]}>
                                نوع مصدر بيانات المؤشر
                              </Text>
                            </View>
                            <Text style={{
                              fontSize: 12,
                              color: themeColors.placeholder,
                              textAlign: 'right',
                              marginBottom: 8,
                            }}>
                              مستقل = استبيان/دراسة/تقرير خارجي (إدخال يدوي + دليل) • تجميعي = يُحسب تلقائيا من المبادرات
                            </Text>
                            {Platform.OS === 'web' ? (
                              <select
                                value={kpi.dataSourceType}
                                onChange={(e) => updateKpi(kpi.id, 'dataSourceType', e.target.value)}
                                dir="rtl"
                                style={{
                                  width: '100%',
                                  padding: '10px 12px',
                                  border: `1px solid ${themeColors.inputBorder}`,
                                  borderRadius: 8,
                                  backgroundColor: themeColors.input,
                                  color: themeColors.inputText,
                                  fontSize: 14,
                                  fontFamily: 'inherit',
                                  textAlign: 'right',
                                  minHeight: 44,
                                  cursor: 'pointer',
                                  outline: 'none',
                                }}
                              >
                                <option value="">— اختر —</option>
                                <option value="independent">مستقل</option>
                                <option value="aggregate">تجميعي من المبادرات</option>
                              </select>
                            ) : (
                              <Text style={{ padding: 10, color: themeColors.inputText }}>
                                {kpi.dataSourceType === 'independent'
                                  ? 'مستقل'
                                  : kpi.dataSourceType === 'aggregate'
                                    ? 'تجميعي من المبادرات'
                                    : '— اختر —'}
                              </Text>
                            )}
                          </View>

                          {/* Implementing section — DTS section 4.3 (only when
                              source is independent). The chosen section is
                              the one entering periodic values + evidence. */}
                          {kpi.dataSourceType === 'independent' && (
                            <View style={{ marginTop: 12 }}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>
                                  القسم المنفذ للمؤشر
                                </Text>
                              </View>
                              <Text style={{
                                fontSize: 12,
                                color: themeColors.placeholder,
                                textAlign: 'right',
                                marginBottom: 8,
                              }}>
                                القسم المسؤول عن إدخال قيمة المؤشر يدويا في كل دورة قياس.
                              </Text>
                              <SingleSelectDropdown
                                value={kpi.implementingSection}
                                options={sectionUnits.map(u => ({
                                  value: u.id.toString(),
                                  label: u.name_ar || u.name,
                                }))}
                                placeholder="اختر القسم المنفذ"
                                onChange={(v) => updateKpi(kpi.id, 'implementingSection', v)}
                              />
                            </View>
                          )}

                          {/* Cascade — DTS UC-02 step 4: pick the sections this
                              strategic KPI is projected onto. Section heads
                              of selected sections will see it in their
                              "إضافة مبادرة" KPI picker. Same chip-style
                              dropdown used by AddInitiativeScreen for
                              "الأقسام المتعاونة". */}
                          <View style={{ marginTop: 12 }}>
                            <View style={styles.labelRow}>
                              <Text style={[styles.label, { color: themeColors.label }]}>
                                إسقاط المؤشر على الأقسام
                              </Text>
                            </View>
                            <Text style={{
                              fontSize: 12,
                              color: themeColors.placeholder,
                              textAlign: 'right',
                              marginBottom: 8,
                            }}>
                              اختر قسما واحدا أو أكثر — رؤساء الأقسام المختارة سيتمكنون من اقتراح مبادرات لخدمة هذا المؤشر.
                            </Text>
                            <MultiSelectDropdown
                              values={kpi.assignedSections}
                              options={sectionUnits.map(u => ({
                                value: u.id.toString(),
                                label: u.name_ar || u.name,
                              }))}
                              placeholder="اختر الأقسام المُسقَط عليها"
                              onChange={(next) => updateKpi(kpi.id, 'assignedSections', next)}
                            />
                          </View>
                        </View>
                      </View>
                    )}
                  </View>
                ))}

                {/* Total weight indicator — must NOT exceed 100% per DTS UC-02 step 4 */}
                {(() => {
                  const strategicKpis = kpis.filter(k => k.kpiType === 'strategic' && k.name.trim());
                  if (strategicKpis.length === 0) return null;
                  const total = strategicKpis.reduce((s, k) => s + (parseFloat(k.weight) || 0), 0);
                  const isExactly100 = Math.abs(total - 100) < 0.01;
                  const isOver = total > 100.01;
                  const isUnder = total < 100 && !isExactly100;
                  return (
                    <View style={{
                      flexDirection: 'row-reverse',
                      alignItems: 'center',
                      gap: 8,
                      paddingHorizontal: 12,
                      paddingVertical: 10,
                      borderRadius: 8,
                      marginBottom: 12,
                      backgroundColor: isOver ? '#FEF2F2' : isExactly100 ? '#F0FDF4' : '#FFFBEB',
                      borderWidth: 1,
                      borderColor: isOver ? '#EF4444' : isExactly100 ? '#22C55E' : '#EAB308',
                    }}>
                      <Text style={{
                        fontSize: 13,
                        fontFamily: FONTS.regular,
                        color: isOver ? '#B91C1C' : isExactly100 ? '#15803D' : '#92400E',
                        flex: 1,
                        textAlign: 'right',
                      }}>
                        {isOver
                          ? `✗ مجموع الأوزان: ${total.toFixed(2)}% — لا يمكن أن يتجاوز 100%`
                          : isExactly100
                            ? `✓ مجموع أوزان المؤشرات: ${total.toFixed(2)}% (مكتمل)`
                            : `مجموع الأوزان: ${total.toFixed(2)}% — متبقي ${(100 - total).toFixed(2)}% (الحد الأقصى 100%)`
                        }
                      </Text>
                    </View>
                  );
                })()}

                {/* Add KPI Button */}
                <TouchableOpacity style={[styles.addKpiButton, { borderColor: themeColors.inputBorder }]} onPress={addKpi}>
                  <Text style={[styles.addKpiButtonText, { color: themeColors.inputText }]}>{t('kpis.addPerformanceIndicator')}</Text>
                  <PlusIcon size={16} color={themeColors.inputText} />
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Risks Section — inline risk capture, open to all users */}
          <View style={[styles.kpiSection, { backgroundColor: themeColors.section, marginTop: 16 }]}>
            <TouchableOpacity
              style={styles.kpiSectionHeader}
              onPress={() => setRiskSectionExpanded(!riskSectionExpanded)}
            >
              {riskSectionExpanded ? (
                <ChevronUpIcon size={20} color={themeColors.iconColor} />
              ) : (
                <ChevronDownIcon size={20} color={themeColors.iconColor} />
              )}
              <Text style={[styles.kpiSectionTitle, { color: themeColors.sectionTitle }]}>
                {t('risks.relatedRisks')}{risks.length > 0 ? ` (${risks.length})` : ''}
              </Text>
            </TouchableOpacity>

            {riskSectionExpanded && (
              <View style={styles.kpiSectionContent}>
                {risks.map((risk, index) => {
                  const riskValue = risk.probability * risk.impact;
                  return (
                    <View key={risk.id} style={[styles.kpiCard, { backgroundColor: themeColors.kpiCard, borderColor: themeColors.kpiCardBorder }]}>
                      <View style={[styles.kpiCardHeader, { backgroundColor: themeColors.kpiCardHeader, borderBottomColor: themeColors.kpiCardBorder }]}>
                        <View style={styles.kpiCardHeaderLeft}>
                          <TouchableOpacity style={styles.kpiHeaderButton} onPress={() => toggleRiskExpansion(risk.id)}>
                            {risk.isExpanded ? (
                              <ChevronUpIcon size={20} color={themeColors.iconColor} />
                            ) : (
                              <ChevronDownIcon size={20} color={themeColors.iconColor} />
                            )}
                          </TouchableOpacity>
                          <TouchableOpacity style={styles.kpiHeaderButton} onPress={() => removeRisk(risk.id)}>
                            <TrashIcon size={18} color={themeColors.iconColor} />
                          </TouchableOpacity>
                        </View>
                        <View style={styles.kpiCardHeaderRight}>
                          <Text style={[styles.kpiCardName, { color: themeColors.inputText }]}>
                            {risk.name || `${t('risks.riskName')} ${index + 1}`}
                          </Text>
                          <View style={[styles.kpiCardNumber, { backgroundColor: themeColors.kpiNumber }]}>
                            <Text style={[styles.kpiCardNumberText, { color: themeColors.inputText }]}>{index + 1}</Text>
                          </View>
                        </View>
                      </View>

                      {risk.isExpanded && (
                        <View style={styles.kpiCardContent}>
                          {/* Name */}
                          <View style={styles.field}>
                            <View style={styles.labelRow}>
                              <Text style={styles.requiredStar}>*</Text>
                              <Text style={[styles.label, { color: themeColors.inputText }]}>{t('risks.riskName')}</Text>
                            </View>
                            <TextInput
                              style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                              value={risk.name}
                              onChangeText={(v) => updateRisk(risk.id, 'name', v)}
                              placeholder={t('risks.enterRiskName')}
                              placeholderTextColor={themeColors.placeholder}
                            />
                          </View>

                          {/* Type + Owner */}
                          <View style={[styles.row, isMobile && styles.rowMobile]}>
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.inputText }]}>{t('risks.riskType')}</Text>
                              </View>
                              <SingleSelectDropdown
                                value={risk.riskType}
                                placeholder={t('risks.selectRiskType')}
                                onChange={(v) => updateRisk(risk.id, 'riskType', v)}
                                options={[
                                  { value: 'strategic', label: t('risks.typeStrategic') },
                                  { value: 'operational', label: t('risks.typeOperational') },
                                  { value: 'financial', label: t('risks.typeFinancial') },
                                  { value: 'compliance', label: t('risks.typeCompliance') },
                                ]}
                              />
                            </View>
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={[styles.label, { color: themeColors.inputText }]}>{t('risks.riskOwner')}</Text>
                              </View>
                              <TextInput
                                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                                value={risk.riskOwner}
                                onChangeText={(v) => updateRisk(risk.id, 'riskOwner', v)}
                                placeholder={t('risks.riskOwnerPlaceholder')}
                                placeholderTextColor={themeColors.placeholder}
                              />
                            </View>
                          </View>

                          {/* Probability + Impact (1-5) */}
                          <View style={[styles.row, isMobile && styles.rowMobile]}>
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={[styles.label, { color: themeColors.inputText }]}>{t('risks.probability')} (1-5)</Text>
                              </View>
                              <View style={styles.riskScaleRow}>
                                {[1, 2, 3, 4, 5].map((n) => (
                                  <TouchableOpacity
                                    key={n}
                                    style={[styles.riskScaleChip, { borderColor: themeColors.inputBorder }, risk.probability === n && styles.riskScaleChipActive]}
                                    onPress={() => updateRisk(risk.id, 'probability', n)}
                                  >
                                    <Text style={[styles.riskScaleChipText, { color: themeColors.inputText }, risk.probability === n && styles.riskScaleChipTextActive]}>{n}</Text>
                                  </TouchableOpacity>
                                ))}
                              </View>
                            </View>
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={[styles.label, { color: themeColors.inputText }]}>{t('risks.impact')} (1-5)</Text>
                              </View>
                              <View style={styles.riskScaleRow}>
                                {[1, 2, 3, 4, 5].map((n) => (
                                  <TouchableOpacity
                                    key={n}
                                    style={[styles.riskScaleChip, { borderColor: themeColors.inputBorder }, risk.impact === n && styles.riskScaleChipActive]}
                                    onPress={() => updateRisk(risk.id, 'impact', n)}
                                  >
                                    <Text style={[styles.riskScaleChipText, { color: themeColors.inputText }, risk.impact === n && styles.riskScaleChipTextActive]}>{n}</Text>
                                  </TouchableOpacity>
                                ))}
                              </View>
                            </View>
                          </View>

                          {/* Computed risk value */}
                          <Text style={[styles.riskValueHint, { color: themeColors.inputText }]}>
                            قيمة الخطر: {riskValue} ({risk.probability} × {risk.impact})
                          </Text>

                          {/* Expected impact */}
                          <View style={styles.field}>
                            <View style={styles.labelRow}>
                              <Text style={[styles.label, { color: themeColors.inputText }]}>{t('risks.expectedImpact')}</Text>
                            </View>
                            <TextInput
                              style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                              value={risk.expectedImpact}
                              onChangeText={(v) => updateRisk(risk.id, 'expectedImpact', v)}
                              placeholder={t('risks.expectedImpactPlaceholder')}
                              placeholderTextColor={themeColors.placeholder}
                              multiline
                            />
                          </View>

                          {/* Mitigation actions */}
                          <View style={styles.field}>
                            <View style={styles.labelRow}>
                              <Text style={[styles.label, { color: themeColors.inputText }]}>{t('risks.mitigationActions')}</Text>
                            </View>
                            <TextInput
                              style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                              value={risk.mitigationActions}
                              onChangeText={(v) => updateRisk(risk.id, 'mitigationActions', v)}
                              placeholder={t('risks.mitigationActions')}
                              placeholderTextColor={themeColors.placeholder}
                              multiline
                            />
                          </View>
                        </View>
                      )}
                    </View>
                  );
                })}

                {/* Add Risk Button */}
                <TouchableOpacity style={[styles.addKpiButton, { borderColor: themeColors.inputBorder }]} onPress={addRisk}>
                  <Text style={[styles.addKpiButtonText, { color: themeColors.inputText }]}>{t('risks.addRiskToGoal')}</Text>
                  <PlusIcon size={16} color={themeColors.inputText} />
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.actionButtons}>
          <TouchableOpacity
            style={[styles.saveButton, saving && styles.saveButtonDisabled]}
            onPress={handleSave}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.saveButtonText}>{t('common.save')}</Text>
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.cancelButton, { backgroundColor: themeColors.cancelButton, borderColor: themeColors.cancelButtonBorder }]}
            onPress={() => navigation.goBack()}
            disabled={saving}
          >
            <Text style={[styles.cancelButtonText, { color: themeColors.cancelButtonText }]}>{t('common.cancel')}</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
      <KPIDeleteConfirmModal
        visible={kpiPendingDelete !== null}
        kpiId={kpiPendingDelete?.backendId ?? null}
        kpiName={kpiPendingDelete?.name}
        onConfirm={confirmRemoveKpi}
        onCancel={() => setKpiPendingDelete(null)}
      />
    </PageContainer>
  );
}

// ============================================================
// Styles (matching Figma design 2448-138681)
// ============================================================

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    // Padding set dynamically via inline style for responsiveness
    paddingBottom: 48,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  breadcrumbLink: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbActive: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#A9211F',
  },
  // Main Card
  mainCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  pageTitle: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 24,
  },
  // Section
  section: {
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'center',
    marginBottom: 16,
  },
  // Field styles
  field: {
    marginBottom: 16,
  },
  fieldSmall: {
    width: '30%',
    minWidth: 100,
  },
  fieldLarge: {
    flex: 1,
  },
  fieldHalf: {
    flex: 1,
  },
  row: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
  },
  rowMobile: {
    flexDirection: 'column',
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 4,
    marginBottom: 8,
  },
  label: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  requiredStar: {
    fontSize: 14,
    color: '#EF4444',
  },
  // Input styles
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    minHeight: 44,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  // Weight Input
  weightInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  weightInput: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    color: '#414042',
    width: 60,
    textAlign: 'center',
  },
  percentSymbol: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  setWeightButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  setWeightButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  weightCapContainer: {
    marginTop: 6,
  },
  weightCapBar: {
    height: 6,
    backgroundColor: '#E5E7EB',
    borderRadius: 3,
    overflow: 'hidden',
  },
  weightCapFill: {
    height: '100%',
    borderRadius: 3,
  },
  weightCapText: {
    fontSize: 11,
    fontFamily: FONTS.regular,
    marginTop: 3,
    textAlign: 'right',
  },
  // Percent Input
  percentInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  percentInput: {
    flex: 1,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  percentLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  // Date Input
  dateInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    gap: 8,
  },
  dateInput: {
    flex: 1,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  // Dropdown styles
  dropdownContainer: {
    position: 'relative',
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 44,
  },
  dropdownDisabled: {
    opacity: 0.6,
  },
  dropdownButtonText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownPlaceholder: {
    color: '#9CA3AF',
  },
  dropdownItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    minHeight: 44,
    justifyContent: 'center',
  },
  dropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  // KPI Section
  kpiSection: {
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
  },
  kpiSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    gap: 8,
    minHeight: 44,
  },
  kpiSectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'center',
  },
  kpiSectionContent: {
    padding: 16,
    paddingTop: 0,
  },
  // KPI Card
  kpiCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    marginBottom: 12,
  },
  kpiCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  kpiCardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  kpiCardHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  kpiHeaderButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpiCardNumber: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  kpiCardNumberText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
  },
  kpiCardName: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  kpiCardContent: {
    padding: 16,
  },
  // Subsection
  subsection: {
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  subsectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'center',
    marginBottom: 16,
  },
  // Add KPI Button
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    borderStyle: 'dashed',
    backgroundColor: '#FFFFFF',
    minHeight: 44,
  },
  addKpiButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Risk scale (probability/impact 1-5) chips
  riskScaleRow: {
    flexDirection: 'row',
    gap: 8,
  },
  riskScaleChip: {
    flex: 1,
    minWidth: 40,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
  },
  riskScaleChipActive: {
    backgroundColor: COLORS.primary,
    borderColor: COLORS.primary,
  },
  riskScaleChipText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
  },
  riskScaleChipTextActive: {
    color: '#FFFFFF',
  },
  riskValueHint: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 12,
  },
  // Action Buttons
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  saveButton: {
    backgroundColor: '#A9211F',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 12,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  saveButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  saveButtonDisabled: {
    backgroundColor: '#D1D5DB',
  },
  cancelButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 12,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
});
